<script setup>
import { ref, useAttrs, watch } from "vue";
import TheInput from "./TheInput.vue";

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    required: true,
  },
  options: {
    type: Array,
    required: true,
  },
  defaultValue: {
    type: [String, Array],
    required: false,
    default: [],
  },
  name: {
    type: String,
  },
});
const emit = defineEmits(["change"]);
const attrs = useAttrs();

function handleSelect(value) {
  emit("change", value);
}

const value = ref(props.defaultValue);

watch(value, function (value) {
  if (!value || (Array.isArray(value) && value.length === 0)) return;
  emit("change", value);
});

defineExpose({
  type: props.type,
  reset() {
    value.value = undefined;
  },
});
</script>

<template>
  <details :class="$style.wrapper" v-bind="attrs">
    <summary :class="$style.title" v-text="props.title" />
    <fieldset :class="$style.group">
      <template v-if="props.type === 'datalist'">
        <TheInput
          type="text"
          :options="props.options"
          @select="handleSelect"
          only-options
          :default-value="props.defaultValue"
          :name="props.name"
        />
      </template>
      <template v-else-if="props.type === 'checkbox' || props.type === 'radio'">
        <TheInput
          ref="refs"
          v-for="option of props.options"
          :key="option.id"
          v-model="value"
          :type="props.type"
          :label="option.title"
          :value="option.id"
          :name="props.name"
        />
      </template>
    </fieldset>
  </details>
</template>

<style module>
.wrapper {
  padding: 20px 0;
  border-bottom: 1px solid #ccd0d1;
}

.wrapper:last-of-type {
  border-bottom: none;
}

.title {
  list-style-type: none;
  display: block;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  color: inherit;
  cursor: pointer;
  transition: color 0.2s ease-in-out;
  user-select: none;
}

.title:hover {
  color: var(--color-red-base);
}

.title::after {
  content: "";
  display: block;
  float: right;
  width: 1em;
  height: 1em;
  margin-left: 1em;
  background-position: center;
  background-repeat: no-repeat;
  background-size: auto;
  background-image: url("data:image/svg+xml,%3Csvg width='11' height='6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.805 4.992l-4-4-4 4' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  transform: rotate(0deg);
  transition: transform 0.2s ease-in-out;
}

.wrapper[open] .title::after {
  transform: rotate(180deg);
}

.group {
  display: flex;
  flex-direction: column;
  row-gap: 12px;
  margin: 16px 0 0;
  padding: 0;
  border: none;
}
</style>
